import type { Product } from '@/components/riffle/types'

// ─── §5 Image assets (local, 1-indexed in card order) ───────
// Card art:  /public/cards/NN.jpg     (768²)
// Hi-res:    /public/cards/hi/NN.jpg  (2560²)
// `n` is the 1-based card number (index + 1).

const pad = (n: number): string => String(n).padStart(2, '0')

export const cardUrl = (n: number): string => `/cards/${pad(n)}.jpg`
export const hiresUrl = (n: number): string => `/cards/hi/${pad(n)}.jpg`

// One shared grade on every product image (§3).
export const IMG_GRADE = 'saturate(0.9) contrast(1.06) brightness(0.97)'

// ─── §6 Product data (index order is load-bearing) ──────────

export const PRODUCTS: Product[] = [
    {
        title: 'Halo',
        artist: 'Smart Home',
        description:
            'A wireless security camera milled from a single block of soft-white polymer.',
        price: '$249',
        specs: [
            { label: 'Sensor', value: '2K HDR' },
            { label: 'Field', value: '150°' },
            { label: 'Power', value: 'Wireless' }
        ],
        detail:
            'Carved from a single billet of soft-touch polymer, Halo hides its lens behind a seamless face — no screws, no seams, no blinking lights. It wakes only to motion, streams in crisp 2K, and runs about ninety days between charges.',
        photo: '1727953990547-420b74fbfbc4'
    },
    {
        title: 'Hush',
        artist: 'Audio',
        description:
            'Over-ear headphones in bone-cream — machined yokes, memory-foam cups.',
        price: '$399',
        specs: [
            { label: 'Drivers', value: '40 mm' },
            { label: 'Battery', value: '60 hr' },
            { label: 'Weight', value: '248 g' }
        ],
        detail:
            'Hush pairs machined aluminium yokes with memory-foam cups that all but vanish on the ears. Adaptive noise-cancelling folds the room away, and sixty hours of playback means you rarely meet the cable.',
        photo: '1693621947585-7b7d94149af4'
    },
    {
        title: 'Bend',
        artist: 'Seating',
        description:
            'A bent-ply lounge chair — one continuous curve, no visible fasteners.',
        price: '$1,180',
        specs: [
            { label: 'Material', value: 'Bent ply' },
            { label: 'Finish', value: 'Matte white' },
            { label: 'Size', value: '74×68×71' }
        ],
        detail:
            'A single sheet of steam-bent ply drawn into one continuous curve, with no visible fasteners anywhere. Bend gives a gentle flex as you settle in, and its open frame keeps it feeling light in a room.',
        photo: '1693387593160-148335f1c07b'
    },
    {
        title: 'Mist',
        artist: 'Care',
        description:
            'A refillable aluminium mister with a satin-anodised teal finish.',
        price: '$68',
        specs: [
            { label: 'Volume', value: '100 ml' },
            { label: 'Finish', value: 'Satin teal' },
            { label: 'Refill', value: 'Yes' }
        ],
        detail:
            'A refillable mister machined from solid aluminium and finished in satin-anodised teal. Its fine, even atomiser makes a little go a long way — built to be topped up for years rather than thrown away.',
        photo: '1657636246856-6f462abd91e0'
    },
    {
        title: 'Pour',
        artist: 'Kitchen',
        description:
            'A borosilicate kettle with a cobalt tint and a minimal swan spout.',
        price: '$129',
        specs: [
            { label: 'Capacity', value: '1.2 L' },
            { label: 'Glass', value: 'Borosilicate' },
            { label: 'Tint', value: 'Cobalt' }
        ],
        detail:
            'Hand-formed borosilicate glass with a cobalt tint and a slim swan spout for a slow, precise pour. The pared-back body shows the water and nothing else, and shrugs off thermal shock.',
        photo: '1712168044214-f5a272c23a5b'
    },
    {
        title: 'Shell',
        artist: 'Seating',
        description:
            'A monoshell chair in matte white on a powder-coated steel base.',
        price: '$640',
        specs: [
            { label: 'Shell', value: 'Recycled poly' },
            { label: 'Base', value: 'Powder steel' },
            { label: 'Size', value: '78×70×80' }
        ],
        detail:
            'Shell is a single moulded seat in matte recycled polymer, cradled on a slender powder-coated steel base. It tilts a few forgiving degrees as you lean back, and wipes clean in a single pass.',
        photo: '1666618207644-4de0226a3f85'
    },
    {
        title: 'Aura',
        artist: 'Lighting',
        description:
            'A frosted ambient lamp that washes a room from warm amber to soft violet.',
        price: '$180',
        specs: [
            { label: 'Light', value: '16M colours' },
            { label: 'Modes', value: 'Scenes' },
            { label: 'Base', value: 'Stone' }
        ],
        detail:
            'A frosted glass dome on a stone base, Aura fills a room with a slow gradient of light — warm amber up top melting into pink and violet below. Tap to cycle through scenes, or dim it down to a low evening glow.',
        photo: '1563237602-b68d2dfb3f3b'
    },
    {
        title: 'Crema',
        artist: 'Kitchen',
        description:
            'A compact espresso machine with a sage-green powder-coat body.',
        price: '$520',
        specs: [
            { label: 'Pressure', value: '9 bar' },
            { label: 'Tank', value: '1.4 L' },
            { label: 'Body', value: 'Sage' }
        ],
        detail:
            'A compact nine-bar espresso machine wrapped in a calm sage powder-coat. Crema pulls a proper café shot from a small footprint, and its 1.4-litre tank keeps the counter clear between refills.',
        photo: '1752134594016-f4969838b68d'
    },
    {
        title: 'Glow',
        artist: 'Lighting',
        description:
            'A floating pendant — a thin halo of warm light on a slim cord.',
        price: '$310',
        specs: [
            { label: 'Light', value: '2700 K' },
            { label: 'Dimming', value: '0–100%' },
            { label: 'Cord', value: '2 m' }
        ],
        detail:
            'A thin halo of warm light suspended on a slim cord — Glow reads as little more than a line and a ring. It dims smoothly from a candle-low ember to a full, even wash across a room.',
        photo: '1530604021746-cc55bfc51987'
    },
    {
        title: 'Vessel',
        artist: 'Object',
        description:
            'A hand-thrown ceramic vessel glazed in chalk-matte white.',
        price: '$96',
        specs: [
            { label: 'Material', value: 'Stoneware' },
            { label: 'Glaze', value: 'Chalk' },
            { label: 'Height', value: '22 cm' }
        ],
        detail:
            'Hand-thrown and finished in a chalk-matte glaze that catches shadow rather than shine. Each Vessel keeps the faint throwing lines of the wheel, so no two are ever quite identical.',
        photo: '1761830476467-0ff86dbcc75d'
    },
    {
        title: 'Onyx',
        artist: 'Audio',
        description:
            'Studio headphones in anodised graphite with magnetic ear pads.',
        price: '$449',
        specs: [
            { label: 'Drivers', value: '45 mm' },
            { label: 'Pads', value: 'Magnetic' },
            { label: 'Cable', value: 'Detachable' }
        ],
        detail:
            'Reference-tuned 45 mm drivers sit in an anodised graphite shell, with magnetic pads that swap in a second and a fully detachable cable. Built for long sessions and honest, uncoloured sound.',
        photo: '1620313491848-7152340cd3ae'
    },
    {
        title: 'Pod',
        artist: 'Object',
        description: 'A rounded desk speaker in terracotta on a stone plinth.',
        price: '$149',
        specs: [
            { label: 'Output', value: '12 W' },
            { label: 'Finish', value: 'Terracotta' },
            { label: 'Plinth', value: 'Stone' }
        ],
        detail:
            'A rounded full-range speaker in warm terracotta, lifted on a small stone plinth that quiets desk resonance. Twelve watts of surprisingly full sound from a body that fits in one hand.',
        photo: '1752134593976-603769edccbb'
    },
    {
        title: 'Curve',
        artist: 'Seating',
        description:
            'A cantilever chair in burnt orange — sprung steel and moulded foam.',
        price: '$560',
        specs: [
            { label: 'Frame', value: 'Sprung steel' },
            { label: 'Foam', value: 'Moulded' },
            { label: 'Size', value: '55×60×82' }
        ],
        detail:
            'Curve floats on a single loop of sprung steel that gives the faintest bounce as you sit. Moulded foam in burnt orange holds its shape through long dinners and longer conversations.',
        photo: '1647009822729-0076c73fe6f0'
    },
    {
        title: 'Stack',
        artist: 'Object',
        description: 'Nesting paper-pulp cylinders — a modular desk-tidy system.',
        price: '$48',
        specs: [
            { label: 'Set', value: '3 pieces' },
            { label: 'Material', value: 'Paper pulp' },
            { label: 'Use', value: 'Desk-tidy' }
        ],
        detail:
            'Three nesting cylinders moulded from recycled paper pulp — a modular desk-tidy that breaks apart to hold pens, cables, or nothing at all. Light, matte to the touch, and quietly recyclable.',
        photo: '1769509071050-dae8fcd6566d'
    },
    {
        title: 'Ember',
        artist: 'Home',
        description: 'A two-wick soy candle set in a speckled stoneware bowl.',
        price: '$42',
        specs: [
            { label: 'Wax', value: 'Soy blend' },
            { label: 'Wicks', value: 'Two' },
            { label: 'Burn', value: '50 hr' }
        ],
        detail:
            'A two-wick candle poured into a speckled stoneware bowl you’ll keep long after the wax is gone. The soy blend burns clean and even for around fifty hours, with a low, steady flame.',
        photo: '1698995475730-0e64a620337b'
    },
    {
        title: 'Fern',
        artist: 'Object',
        description: 'A glass diffuser in forest green on a soft gradient stage.',
        price: '$88',
        specs: [
            { label: 'Volume', value: '180 ml' },
            { label: 'Glass', value: 'Hand-blown' },
            { label: 'Mist', value: 'Cool' }
        ],
        detail:
            'Hand-blown forest-green glass sits over a cool ultrasonic mist, so Fern scents a room without any heat. The water level glows softly through the glass as it quietly works.',
        photo: '1649371568175-701146e6801f'
    },
    {
        title: 'Slate',
        artist: 'Care',
        description: 'A matte-black grooming bottle with a magnetic cap.',
        price: '$54',
        specs: [
            { label: 'Volume', value: '200 ml' },
            { label: 'Cap', value: 'Magnetic' },
            { label: 'Finish', value: 'Matte' }
        ],
        detail:
            'A matte-black bottle with a magnetic cap that lands with a satisfying click and never rolls off the shelf. Refillable, and sized for the dopp kit as easily as the bathroom shelf.',
        photo: '1593210435269-fc1e75b23610'
    },
    {
        title: 'Field',
        artist: 'Audio',
        description:
            'A rugged portable speaker in speckled stone with a woven carry strap.',
        price: '$159',
        specs: [
            { label: 'Output', value: '20 W' },
            { label: 'Rating', value: 'IP67' },
            { label: 'Strap', value: 'Woven' }
        ],
        detail:
            'Cast in a speckled stone composite and rated IP67, Field shrugs off rain, dust, and the odd drop. A woven strap clips it to a pack, and twenty watts carry cleanly outdoors.',
        photo: '1690802220555-669d067fd8ec'
    },
    {
        title: 'Cube',
        artist: 'Device',
        description: 'A palm-sized smart speaker wrapped in acoustic felt.',
        price: '$129',
        specs: [
            { label: 'Output', value: '16 W' },
            { label: 'Wrap', value: 'Felt' },
            { label: 'Voice', value: 'Built-in' }
        ],
        detail:
            'A palm-sized speaker wrapped in acoustic felt that softens both how it sounds and how it looks. A voice assistant lives inside, it fills a room for its size, and it’s at home on any shelf.',
        photo: '1777573361257-7560a1629701'
    },
    {
        title: 'Haze',
        artist: 'Home',
        description: 'A stoneware aroma diffuser that breathes a fine, cool mist.',
        price: '$74',
        specs: [
            { label: 'Volume', value: '160 ml' },
            { label: 'Body', value: 'Stoneware' },
            { label: 'Run', value: '8 hr' }
        ],
        detail:
            'Haze breathes a fine, cool mist from a speckled stoneware body — part diffuser, part quiet object. A single fill lasts about eight hours, controlled by two understated touch keys.',
        photo: '1581235720704-06d3acfcb36f'
    },
    {
        title: 'Balm',
        artist: 'Care',
        description: 'A refillable body-lotion pump in oat-speckled stoneware.',
        price: '$36',
        specs: [
            { label: 'Volume', value: '500 ml' },
            { label: 'Bottle', value: 'Stoneware' },
            { label: 'Refill', value: 'Yes' }
        ],
        detail:
            'A refillable body-lotion pump in oat-speckled stoneware, meant to live on the counter rather than hide in a cupboard. Twist the collar to lock the pump flat for travel.',
        photo: '1747317031830-d90bcdbd4407'
    },
    {
        title: 'Stow',
        artist: 'Object',
        description:
            'A rounded travel case in speckled sandstone with a soft-touch latch.',
        price: '$120',
        specs: [
            { label: 'Material', value: 'Resin' },
            { label: 'Latch', value: 'Soft-touch' },
            { label: 'Use', value: 'Travel' }
        ],
        detail:
            'A rounded travel case in a speckled sandstone-look resin, with a soft-touch latch that opens one-handed. Made to corral the small things you’d otherwise lose at the bottom of a bag.',
        photo: '1698663371785-677d13f21570'
    }
]

// ─── §6 Ambient tint colours (border-sampled RGB, card order) ───

export const TINTS: [number, number, number][] = [
    [183, 166, 148],
    [206, 196, 184],
    [155, 183, 182],
    [26, 51, 55],
    [201, 157, 157],
    [54, 53, 52],
    [163, 152, 153],
    [159, 159, 143],
    [96, 122, 141],
    [104, 84, 65],
    [122, 128, 132],
    [153, 102, 70],
    [157, 166, 138],
    [184, 172, 156],
    [113, 88, 67],
    [74, 101, 105],
    [95, 98, 99],
    [151, 143, 135],
    [80, 49, 53],
    [188, 185, 182],
    [201, 188, 175],
    [129, 123, 107]
]

export const N = PRODUCTS.length // 22
export const HERO = 0
